import { useState } from 'react';
import { Bell, BellOff } from 'lucide-react';
import TanoCharacter from '../../components/Eleve/TanoCharacter';
import { isSfxMuted, setSfxMuted } from '../../utils/xpSound';
import { nomPalier } from '../../utils/ileLore';

/**
 * HUD persistant de l'espace élève (sticky en haut), façon mockup.
 * Branché sur la gamification réelle (profil XP du back) : avatar Tano avec
 * skin équipé, niveau, barre XP, série 🔥, étoiles ⭐ — repli 0 (atténué)
 * tant que le profil n'est pas chargé. Toggle des effets sonores partagé
 * avec la passation (localStorage xpSfxMuted, relu à chaque lecture).
 */
function HeaderEleve({ prenom, classe, xp, progress, niveau, serie, etoiles, plume, acc, aura }) {
  const hasXp = typeof xp === 'number';
  // Largeur de la barre = `progress` (ratio 0-100 vers le niveau suivant) si fourni,
  // sinon repli sur `xp` (rétro-compat). Le libellé affiche l'XP totale réelle.
  const barWidth = typeof progress === 'number' ? progress : (hasXp ? xp : 0);
  const [sfxMuted, setSfxMutedState] = useState(isSfxMuted());
  const toggleSfx = () => {
    const next = !sfxMuted;
    setSfxMuted(next);
    setSfxMutedState(next);
  };

  return (
    <header
      className="eleve-gamified sticky top-0 z-30"
      style={{ background: 'rgba(10,31,61,.92)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(41,182,232,.35)' }}
    >
      <div className="w-full max-w-md xl:max-w-4xl mx-auto px-4 sm:px-6 xl:px-10 py-2.5 flex items-center gap-3">
        {/* Avatar = personnage Tano (skin équipé) */}
        <div
          className="shrink-0 rounded-full flex items-center justify-center"
          style={{ width: 46, height: 46, background: 'radial-gradient(circle at 30% 25%,#29B6E8,#122E54)', border: '3px solid #FFB938' }}
        >
          <TanoCharacter plume={plume} acc={acc} aura={aura} size={40} />
        </div>

        {/* Nom · classe + niveau + barre XP */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-white font-bold text-sm truncate" style={{ fontFamily: 'Fredoka' }}>
              {prenom}{classe ? ` · ${classe}` : ''}
            </span>
            <span className="text-[10px] font-extrabold px-1.5 py-px rounded-full shrink-0 max-w-[11rem] truncate" style={{ background: 'rgba(255,185,56,.16)', color: '#FFB938' }} title={nomPalier(niveau).nom}>
              Niveau {niveau ?? 1} · {nomPalier(niveau).emoji} {nomPalier(niveau).nom}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2">
            <div className="h-2.5 flex-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,.16)' }}>
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${Math.max(0, Math.min(100, barWidth))}%`, background: 'linear-gradient(90deg,#F97316,#FFB938)' }}
              />
            </div>
            <span className="text-[9px] font-bold shrink-0" style={{ color: 'rgba(255,255,255,.6)' }}>
              {`${hasXp ? xp : 0} XP`}
            </span>
          </div>
        </div>

        {/* Sons on/off (partagé avec la passation via localStorage) */}
        <button
          type="button"
          onClick={toggleSfx}
          aria-label={sfxMuted ? 'Activer les sons' : 'Couper les sons'}
          className="shrink-0 rounded-full p-1.5 transition hover:opacity-80"
          style={{ background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.15)', color: sfxMuted ? 'rgba(255,255,255,.45)' : '#29B6E8' }}
        >
          {sfxMuted ? <BellOff size={15} /> : <Bell size={15} />}
        </button>

        {/* Série / étoiles (atténuées à 0) */}
        <div className="flex flex-col items-end gap-0.5 shrink-0 text-[12px] font-extrabold" style={{ fontFamily: 'Fredoka' }}>
          <span style={{ color: '#F97316', opacity: (serie ?? 0) > 0 ? 1 : 0.45 }}>🔥 {serie ?? 0}</span>
          <span style={{ color: '#FFB938', opacity: (etoiles ?? 0) > 0 ? 1 : 0.45 }}>⭐ {etoiles ?? 0}</span>
        </div>
      </div>
    </header>
  );
}

export default HeaderEleve;
